import React, { useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { LocationOn, Phone, Email } from "@mui/icons-material";

const FormContainer = styled(Box)(() => ({
  maxWidth: "1200px",
  margin: "50px auto",
  padding: "20px",
  display: "flex",
  gap: "20px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  flexDirection: "column",
  "@media (min-width: 768px)": {
    flexDirection: "row",
  },
}));

const SubmitButton = styled(Button)(() => ({
  backgroundColor: "#98AF41",
  color: "#fff",
  fontWeight: "bold",
  padding: "10px 20px",
  borderRadius: "20px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
  marginTop: "20px",
  "&:hover": {
    backgroundColor: "#7e9635",
  },
}));

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { fullName, email, subject, message } = formData;

    const whatsappMessage = `Hello, I am ${fullName}. %0A%0A` +
      `Email: ${email} %0A%0A` +
      `Subject: ${subject} %0A%0A` +
      `Message: ${message}`;

    // WhatsApp URL format with the phone number and message
    const whatsappUrl = `https://wa.me/+971524346217?text=${encodeURIComponent(whatsappMessage)}`;

    // Redirect to WhatsApp
    window.open(whatsappUrl, "_blank");
  };

  return (
    <FormContainer>
      {/* Left Side: Contact Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Heading */}
        <Typography
          variant="h4"
          sx={{ color: "#98AF41", marginBottom: "20px" ,fontFamily: '"Montserrat", sans-serif',}}
        >
          <b>Say Hello,</b>
        </Typography>

        {/* Form Fields */}
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* Full Name and Email Address */}
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              label="Full Name"
              variant="outlined"
              fullWidth
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              InputLabelProps={{ style: { color: "#98AF41" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#98AF41",
                  },
                },
              }}
            />
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              name="email"
              value={formData.email}
              onChange={handleChange}
              InputLabelProps={{ style: { color: "#98AF41" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#98AF41",
                  },
                },
              }}
            />
          </Box>

          {/* Message Subject */}
          <TextField
            label="Message Subject"
            variant="outlined"
            fullWidth
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            InputLabelProps={{ style: { color: "#98AF41" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#98AF41",
                },
              },
            }}
          />

          {/* Message */}
          <TextField
            label="Message"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            name="message"
            value={formData.message}
            onChange={handleChange}
            InputLabelProps={{ style: { color: "#98AF41" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#98AF41",
                },
              },
            }}
          />

          {/* Submit Button */}
          <SubmitButton type="submit" variant="contained">
            Submit
          </SubmitButton>
        </Box>
      </Box>

      {/* Right Side: Contact Info and Google Map */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Typography variant="h6" sx={{ color: "#98AF41",fontFamily: '"Montserrat", sans-serif', }}>
          <b>Contact Information</b>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <LocationOn sx={{ color: "#98AF41" }} />
          <Typography><b>Address</b>: CGL GENERAL TRADING LLC
            UMM-RAMOOL, DUBAI, UAE</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Phone sx={{ color: "#98AF41" }} />
          <Typography><b>Phone</b>: +971524346217</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Phone sx={{ color: "#98AF41" }} />
          <Typography><b>Landline</b>: +97145588324</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Email sx={{ color: "#98AF41" }} />
          <Typography><b>Email</b>: cglholdings@aol.com</Typography>
        </Box>

        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            height: "300px",
          }}
        >
          <iframe
            title="Google Map"
            src="https://www.google.com/maps/embed?pb=!1m13!1m8!1m3!1d3609.4619572857696!2d55.3731751!3d25.2213611!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjXCsDEzJzE2LjkiTiA1NcKwMjInMzIuNyJF!5e0!3m2!1sen!2sin!4v1737105163173!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen="true"
            loading="lazy"
          ></iframe>
        </Box>
      </Box>
    </FormContainer>
  );
};

export default ContactForm;
